<template>
  <b-overlay spinner-variant="primary" :show="isLoading">
    <div class="d-flex align-items-center mb-2">
      <h1 class="mb-0 mr-2">
        {{ service.name }}
      </h1>
      <b-form-checkbox
        v-model="service.disabled"
        switch
        size="lg"
        class="ml-auto"
      >
        <span :class="service.disabled ? 'text-danger' : 'text-success'">
          {{ service.disabled ? 'Вимкнено' : 'Увімкнено' }}
        </span>
      </b-form-checkbox>
    </div>

    <b-alert
      variant="warning"
      :show="hasUnsavedChanges"
      class="py-1 px-1 mb-2"
    >
      <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50" />
      У вас є незбережені зміни. Не забудьте натиснути "Зберегти зміни".
    </b-alert>

    <div class="row">
      <div class="col-12">
        <b-card title="Видима назва послуги">
          <b-form-input v-model="service.name" />
        </b-card>
      </div>
      <div class="col-12">
        <b-card title="Короткий опис послуги">
          <b-form-textarea v-model="service.description"></b-form-textarea>
        </b-card>
      </div>
      <div class="col-12" v-if="service.slug == 'dwelling-queue-information'">
        <b-card title="Файл черги">
          <b-alert
            variant="warning"
            show
            class="p-1"
            v-if="service.files && service.files.length > 0"
          >
            <div
              class="file d-flex align-items-center w-100"
              v-for="(file, i) in service.files"
              :key="i"
            >
              <strong>
                {{ file.file_name }}
                <small
                  >({{
                    file.added
                      ? $moment(file.added).format("DD.MM.YYYY HH:mm")
                      : "не вказано"
                  }})</small
                >
              </strong>

              <div class="actions ml-auto">
                <b-button
                  size="sm"
                  class="mr-1"
                  :href="file.file_url"
                  target="_blank"
                >
                  Відкрити
                </b-button>
                <b-button size="sm" variant="danger" @click="removeFile(i)"
                  >Видалити</b-button
                >
              </div>
            </div>
          </b-alert>
          <file-uploader
            v-if="!service.files || (service.files && service.files.length < 1)"
            :max-files-count="1"
            @update="updateServiceFiles"
          />
        </b-card>
      </div>
      <div class="col-12">
        <b-card title="Про сервіс">
          <!-- Or manually control the data synchronization -->
          <quill-editor
            :content="service.about"
            :options="editorOption"
            @change="onEditorChange($event)"
          />
        </b-card>
      </div>
      <div class="col-12">
        <b-card title="Відділ, куди направляти заявки за замовчуванням">
          <b-alert show variant="warning" class="py-1 px-1">
            <strong>УВАГА!</strong> Якщо призначити відділ за замовчуванням,
            користувач не зможе обрати інший відділ
          </b-alert>
          <v-select
            id="department-parent"
            multiple
            v-model="service.default_department"
            :options="departments"
            :reduce="(text) => text.value"
            label="text"
            placeholder="Оберіть відділ (необов'язково)"
          />
        </b-card>
      </div>
      <div class="col-12">
        <b-card title="Файли та документи">
          <b-alert variant="info" :show="!service.files_info || !service.files_info.length" class="py-1 px-1">
            Файлів ще немає.
          </b-alert>
          <div
            v-for="(item, fi) in (service.files_info || [])"
            :key="fi"
            class="border rounded p-1 mb-1"
          >
            <div class="d-flex align-items-start">
              <div class="flex-grow-1">
                <h5 class="mb-25">{{ item.label }}</h5>
                <p v-if="item.description" class="text-muted mb-50">{{ item.description }}</p>
                <div v-if="item.file && item.file.file_url">
                  <b-button
                    size="sm"
                    variant="outline-primary"
                    :href="item.file.file_url"
                    target="_blank"
                    class="mr-50"
                  >
                    {{ item.file.file_name || 'Відкрити файл' }}
                  </b-button>
                  <small class="text-muted" v-if="item.file.added">
                    {{ $moment(item.file.added).format("DD.MM.YYYY HH:mm") }}
                  </small>
                </div>
                <b-alert v-else variant="warning" show class="py-50 px-1 mb-0 mt-50">
                  Файл не завантажено
                </b-alert>
              </div>
              <div class="d-flex">
                <b-button
                  size="sm"
                  variant="flat-primary"
                  class="btn-icon p-25"
                  @click="editFileInfo(fi)"
                >
                  <feather-icon icon="EditIcon" size="14" />
                </b-button>
                <b-button
                  size="sm"
                  variant="flat-danger"
                  class="btn-icon p-25"
                  @click="removeFileInfo(fi)"
                >
                  <feather-icon icon="TrashIcon" size="14" />
                </b-button>
              </div>
            </div>
          </div>
          <b-button variant="outline-success" size="sm" @click="addFileInfo">
            + Додати файл
          </b-button>
        </b-card>
      </div>
      <div class="col-12">
        <b-card title="Конструктор форми заявки">
          <form-constructor v-model="service.form_fields" />
        </b-card>
      </div>
      <div class="col-12">
        <div class="d-flex align-items-center justify-content-end">
          <small v-if="hasUnsavedChanges" class="text-warning mr-1">
            <feather-icon icon="AlertTriangleIcon" size="14" class="mr-25" />
            Є незбережені зміни
          </small>
          <b-button variant="primary" @click="saveService">
            Зберегти зміни
          </b-button>
        </div>
      </div>

    <!-- File info modal -->
    <b-modal
      v-model="showFileInfoModal"
      :title="fileInfoUsecase === 'add' ? 'Додати файл' : 'Редагувати файл'"
      hide-footer
      centered
      no-close-on-backdrop
      size="md"
    >
      <b-form-group label="Назва">
        <b-form-input v-model="currentFileInfo.label" placeholder="Наприклад: Порядок денний" />
      </b-form-group>
      <b-form-group label="Опис (необов'язково)">
        <b-form-textarea v-model="currentFileInfo.description" placeholder="Короткий опис файлу" rows="2" />
      </b-form-group>
      <b-form-group label="Файл">
        <div v-if="currentFileInfo.file && currentFileInfo.file.file_url" class="mb-1">
          <b-button
            size="sm"
            variant="outline-primary"
            :href="currentFileInfo.file.file_url"
            target="_blank"
            class="mr-50"
          >
            {{ currentFileInfo.file.file_name || 'Поточний файл' }}
          </b-button>
          <b-button size="sm" variant="outline-danger" @click="currentFileInfo.file = null">
            Замінити
          </b-button>
        </div>
        <file-uploader
          v-else
          :max-files-count="1"
          @update="onFileInfoUploaded"
        />
      </b-form-group>
      <div class="d-flex justify-content-end mt-2">
        <b-button variant="outline-secondary" class="mr-1" @click="showFileInfoModal = false">
          Скасувати
        </b-button>
        <b-button
          variant="primary"
          :disabled="!currentFileInfo.label"
          @click="saveFileInfo"
        >
          {{ fileInfoUsecase === 'add' ? 'Додати' : 'Зберегти' }}
        </b-button>
      </div>
    </b-modal>
    </div>
  </b-overlay>
</template>

<script>
import "quill/dist/quill.core.css";
import "quill/dist/quill.snow.css";
import "quill/dist/quill.bubble.css";
import { quillEditor } from "vue-quill-editor";
import vSelect from "vue-select";
import fileUploader from "@/components/file-uploader.vue";
import formConstructor from "@/components/form-constructor.vue";
export default {
  components: {
    vSelect,
    quillEditor,
    fileUploader,
    formConstructor,
  },
  data() {
    return {
      isLoading: false,
      service: {},
      originalServiceSnapshot: null,
      all_departments: [],
      editorOption: {
        theme: "snow",
      },
      showFileInfoModal: false,
      fileInfoUsecase: "add",
      fileInfoEditIndex: null,
      currentFileInfo: { label: "", description: "", file: null },
    };
  },
  computed: {
    departments() {
      let list = this.all_departments.filter((e) => {
        return e.is_position;
      });
      list = list.map((e) => {
        return { text: e.name, value: e.id };
      });

      list.unshift({ text: "Немає", value: null });
      return list;
    },
    hasUnsavedChanges() {
      if (!this.originalServiceSnapshot) return false;
      return JSON.stringify(this.service) !== this.originalServiceSnapshot;
    },
  },
  methods: {
    async getDepartments() {
      this.isLoading = true;
      var response = await this.$api.get("/admin/departments");
      this.all_departments = response.data.data;
      this.isLoading = false;
    },
    async getService() {
      this.isLoading = true;
      let slug = this.$route.params.slug;
      let response = await this.$api.get("/admin/services/" + slug);
      if (response.data.data) {
        this.service = response.data.data;
        this.originalServiceSnapshot = JSON.stringify(this.service);
      }
      this.isLoading = false;
    },
    async saveService() {
      this.isLoading = true;
      try {
        await this.$api.post("/admin/services/update", {
          uid: this.service.uid,
          name: this.service.name,
          description: this.service.description,
          about: this.service.about,
          default_department: this.service.default_department,
          form_fields: this.service.form_fields || [],
          files_info: this.service.files_info || [],
          disabled: this.service.disabled || false,
        });
        this.originalServiceSnapshot = JSON.stringify(this.service);
        this.$toast.success("Зміни збережено");
      } catch (e) {
        this.$toast.error("Помилка при збереженні");
      }
      this.isLoading = false;
    },
    onEditorChange(e) {
      this.service.about = e.html;
    },
    async updateServiceFiles(files) {
      let uid = this.service.uid;
      try {
        await this.$api.post("/admin/services/updateFiles", {
          uid: uid,
          files: files,
        });
        this.$toast.success("Файли послуги оновлено");
        window.location.reload();
      } catch (error) {
        this.$toast.error("Помилка при оновленні файлів послуги");
      }
    },
    async removeFile(index) {
      if (!confirm("Ви впевнені, що хочете видалити цей файл?")) {
        return;
      }
      this.isLoading = true;
      let newFiles = Object.assign([], this.service.files);
      newFiles.splice(index, 1);
      await this.updateServiceFiles(newFiles);
    },
    addFileInfo() {
      this.fileInfoUsecase = "add";
      this.fileInfoEditIndex = null;
      this.currentFileInfo = { label: "", description: "", file: null };
      this.showFileInfoModal = true;
    },
    editFileInfo(index) {
      this.fileInfoUsecase = "edit";
      this.fileInfoEditIndex = index;
      this.currentFileInfo = JSON.parse(JSON.stringify(this.service.files_info[index]));
      this.showFileInfoModal = true;
    },
    removeFileInfo(index) {
      if (!confirm("Видалити цей файл?")) return;
      if (!this.service.files_info) return;
      this.service.files_info.splice(index, 1);
    },
    onFileInfoUploaded(files) {
      if (files && files.length > 0) {
        this.currentFileInfo.file = files[0];
      }
    },
    saveFileInfo() {
      const item = JSON.parse(JSON.stringify(this.currentFileInfo));
      if (!this.service.files_info) {
        this.$set(this.service, "files_info", []);
      }
      if (this.fileInfoUsecase === "add") {
        this.service.files_info.push(item);
      } else {
        this.$set(this.service.files_info, this.fileInfoEditIndex, item);
      }
      this.showFileInfoModal = false;
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      if (confirm("У вас є незбережені зміни. Покинути сторінку?")) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  mounted() {
    this.getService();
    this.getDepartments();
  },
};
</script>

<style lang="scss">
@import "~@core/scss/vue/libs/vue-select.scss";
</style>
